import { arrayUnion, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { getMessaging, getToken, isSupported, Messaging, onMessage } from 'firebase/messaging';

import { AdminPushToken, COLLECTIONS } from '@j-s/dr-shared';

import { getFirebaseApp, getFirebaseFirestore } from '../../firebase/config';

let messagingInstance: Messaging | null = null;

export async function getFirebaseMessagingInstance(): Promise<Messaging | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const supported = await isSupported();
  if (!supported) {
    return null;
  }

  if (!messagingInstance) {
    messagingInstance = getMessaging(getFirebaseApp());
  }

  return messagingInstance;
}

export function isPushNotificationSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export async function requestPushPermissionAndGetToken(): Promise<string | null> {
  if (!isPushNotificationSupported()) {
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return null;
  }

  const messaging = await getFirebaseMessagingInstance();
  if (!messaging) {
    return null;
  }

  const swRegistration = await navigator.serviceWorker.ready;
  const vapidKey = process.env['NEXT_PUBLIC_FIREBASE_VAPID_KEY'];

  try {
    const token = await getToken(messaging, {
      serviceWorkerRegistration: swRegistration,
      ...(vapidKey ? { vapidKey } : {}),
    });

    return token || null;
  } catch (err) {
    console.warn('Failed to retrieve FCM token:', err);

    return null;
  }
}

export async function saveClientPushTokenToTicket(
  ticketId: string,
  token: string
): Promise<void> {
  const db = getFirebaseFirestore();
  const docRef = doc(db, COLLECTIONS.MESSAGES, ticketId);

  await updateDoc(docRef, {
    pushTokens: arrayUnion(token),
    lastActivityAt: Date.now(),
  });
}

export async function saveAdminPushToken(
  token: string,
  user: { uid: string; email?: string | null }
): Promise<void> {
  const db = getFirebaseFirestore();
  const tokenDocId = encodeURIComponent(token.slice(-32));
  const docRef = doc(db, COLLECTIONS.ADMIN_PUSH_TOKENS, tokenDocId);

  const payload: AdminPushToken = {
    token,
    uid: user.uid,
    userEmail: user.email || '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  await setDoc(docRef, payload, { merge: true });
}

export async function removeAdminPushToken(token: string): Promise<void> {
  const db = getFirebaseFirestore();
  const tokenDocId = encodeURIComponent(token.slice(-32));
  const docRef = doc(db, COLLECTIONS.ADMIN_PUSH_TOKENS, tokenDocId);

  await deleteDoc(docRef);
}

export async function subscribeToForegroundPush(
  callback: (payload: any) => void
): Promise<(() => void) | null> {
  const messaging = await getFirebaseMessagingInstance();
  if (!messaging) {
    return null;
  }

  return onMessage(messaging, callback);
}
